import { accountGroupOf, normalDirectionsOf } from '@hue/contracts';
import { Combobox } from '@hue/design-system';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { financialClassificationApi } from '../lib/api.js';
import { RequestAlert } from './data-view.js';

/// Integração visual dos formulários financeiros, sem importação entre módulos.
export function useFinancialClassification(
  canClassify: boolean,
  kind: 'OBLIGATION' | 'RECURRENCE',
  id?: string,
  attempted = false,
  direction: 'PAYABLE' | 'RECEIVABLE' = 'PAYABLE',
) {
  const [search, setSearch] = useState('');
  const [choice, setChoice] = useState(id ? 'KEEP' : '');
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const query = useQuery({
    queryKey: ['company', 'accounting', 'form-options', kind, id],
    queryFn: () => financialClassificationApi.options(kind, id),
    enabled: canClassify,
    retry: false,
  });
  const current = query.data?.accounts.find(
    (account) => account.id === query.data?.currentAccountId,
  );
  const accounts = query.data?.accounts ?? [];
  const selectable = accounts.filter(
    (account) =>
      account.active &&
      normalDirectionsOf(account.code).includes(direction === 'PAYABLE' ? 'OUTFLOW' : 'INFLOW') &&
      !accounts.some((child) => child.code.startsWith(`${account.code}.`)),
  );
  const ready = !canClassify || (query.isSuccess && choice !== '');
  const options = {
    idempotencyKey,
    ...(canClassify && choice !== 'KEEP'
      ? { accountingAccountId: choice === 'NONE' ? null : choice }
      : {}),
  };
  const choices = [
    ...(id
      ? [
          {
            value: 'KEEP',
            label: current
              ? `Manter ${current.code} ${current.name}${current.active ? '' : ' (desativada)'}`
              : 'Manter sem classificação própria',
            description: 'Conserva a classificação atual deste cadastro.',
          },
        ]
      : []),
    ...selectable.map((account) => ({
      value: account.id,
      label: `${account.code} ${account.name}`,
      description: exampleOf(account.name) || groupGuidance(account.code),
    })),
    {
      value: 'NONE',
      label: id ? 'Remover classificação própria' : 'Salvar sem conta do plano',
      description: id
        ? 'Retira a escolha feita neste cadastro.'
        : 'Se nenhuma opção corresponder, deixe para classificar depois.',
    },
  ];
  const field = canClassify ? (
    <div className="grid gap-2 sm:col-span-2">
      <Combobox
        label="Conta do plano"
        placeholder="Busque pelo nome ou por um exemplo"
        wrapDescriptions
        error={
          attempted && !ready
            ? 'Escolha a conta do plano ou “Salvar sem conta do plano” para classificar depois.'
            : undefined
        }
        selectedLabel={choices.find((option) => option.value === choice)?.label ?? ''}
        onSelect={(selection) => setChoice(selection.kind === 'option' ? selection.value : '')}
        onClear={() => setChoice('')}
        disabled={query.isPending || query.isError}
        onQueryChange={setSearch}
        options={choices.filter((option) => {
          return fold(`${option.label} ${option.description}`).includes(fold(search));
        })}
      />
      <p className="text-xs text-content-muted">
        {direction === 'PAYABLE' ? 'Escolha o tipo de despesa.' : 'Escolha o tipo de receita.'} Os
        exemplos aparecem na lista.
      </p>
      {kind === 'RECURRENCE' ? (
        <p className="text-xs text-content-muted">
          {id
            ? 'A conta do plano vale para os próximos lançamentos. Os já gerados conservam a classificação anterior.'
            : 'A conta do plano será usada nas despesas geradas por esta recorrência.'}
        </p>
      ) : null}
      {query.isPending && (
        <p className="text-sm text-content-muted">Carregando o plano de contas…</p>
      )}
      {query.isError && (
        <RequestAlert message="Não foi possível carregar o plano de contas. Feche e abra o formulário para tentar novamente." />
      )}
    </div>
  ) : null;
  // A tela só afirma que a conta do plano vale quando existe mesmo uma conta escolhida ou mantida.
  const chosen =
    canClassify && (choice === 'KEEP' ? Boolean(current) : choice !== '' && choice !== 'NONE');
  return { field, ready, options, chosen };
}

function fold(text: string) {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
}
function exampleOf(name: string): string {
  const text = fold(name);
  const specific = explanations.get(text);
  if (specific) return specific;
  if (/insumos|descartaveis|materiais de consumo/u.test(text))
    return 'Exemplos: luvas, algodão e descartáveis usados no serviço.';
  if (/marketing|publicidade/u.test(text)) return 'Exemplos: anúncios, campanhas e divulgação.';
  if (/aluguel/u.test(text)) return 'Exemplo: aluguel do imóvel da empresa.';
  if (/manutencao/u.test(text))
    return 'Exemplos: reparos e conservação. Compra de equipamento novo exige outra classificação.';
  if (/administrativ/u.test(text)) return 'Exemplos: papelaria e materiais administrativos.';
  if (/revenda|estoque/u.test(text))
    return 'Mercadorias para comercialização: a compra para estoque não deve ser tratada automaticamente como despesa operacional.';
  if (/equipamento|mobiliario|imobilizado/u.test(text))
    return 'Exemplos: máquinas, móveis e computadores. Avalie como ativo, sem lançar automaticamente como despesa operacional.';
  return '';
}

// O nome identifica o significado; o código pode ser reutilizado por um plano personalizado.
const explanations = new Map(
  Object.entries({
    Serviços: 'Recebimento por serviços prestados, como aulas, consultas e atendimentos.',
    Produtos: 'Recebimento pela venda de mercadorias, como cosméticos, roupas e acessórios.',
    Pacotes: 'Recebimento pela venda de um conjunto de serviços ou sessões.',
    'Outras receitas operacionais':
      'Recebimentos da atividade da empresa que não correspondem às outras opções de receita.',
    'Retenções por cancelamento':
      'Valor do adiantamento que a empresa pode manter após cancelamento ou falta, conforme a política da venda.',
    'ISS sobre serviços':
      'Dedução da receita: imposto municipal sobre serviços vendidos. Não use para encargos da folha de pagamento.',
    'DAS — Simples Nacional':
      'Guia de tributos do Simples Nacional. Neste plano, fica em Deduções da receita; confirme com a contabilidade se a guia será classificada inteira ou separada por tributo.',
    'Descontos concedidos':
      'Dedução da receita: redução do preço da venda dada ao cliente. Exemplo: desconto de R$ 20 em um serviço.',
    'Devoluções e cancelamentos de vendas':
      'Dedução da receita: valor de uma venda devolvida ou cancelada. Não use para reembolsar uma compra feita por alguém para a empresa.',
    Pessoal:
      'Despesa operacional com a equipe: salários, remuneração dos sócios pelo trabalho, comissões, encargos e benefícios.',
    Salários:
      'Despesa operacional: remuneração dos empregados pelo trabalho. Exemplo: salário mensal da recepção.',
    'Pró-labore':
      'Despesa operacional: remuneração do sócio pelo trabalho na empresa. Distribuição de lucros tem outra classificação.',
    'Comissões de autônomos e PJ':
      'Despesa operacional: comissão paga a autônomo com RPA ou a MEI ou PJ com nota. Outros serviços contratados têm a própria classificação.',
    'Adiantamentos a profissionais':
      'Fora do resultado: vale pago ao profissional por conta da comissão. O custo entra no DRE quando a comissão é apurada, não quando o vale sai.',
    'Comissões fora da folha':
      'Despesa operacional: comissão de empregado paga fora da folha. Entra no resultado da empresa; os reflexos no salário e nos encargos são assunto para o contador.',
    'Comissões de profissionais':
      'Despesa operacional: remuneração por vendas ou atendimentos. Mesmo calculada sobre uma venda, não é desconto dado ao cliente nem dedução da receita.',
    Bonificações:
      'Despesa operacional: prêmios e gratificações pagos à equipe, como bônus por metas.',
    'Encargos (FGTS/INSS)':
      'Despesa operacional: encargos sobre a folha de pagamento, como FGTS e contribuição previdenciária patronal. Não são impostos sobre vendas.',
    'Benefícios (vale-transporte e refeição)':
      'Despesa operacional: benefícios da equipe, como vale-transporte, vale-refeição e alimentação.',
    Ocupação:
      'Despesa operacional para usar e manter o imóvel da empresa, como aluguel, condomínio e conservação.',
    Condomínio: 'Despesa operacional: cobrança de condomínio do imóvel usado pela empresa.',
    IPTU: 'Despesa operacional: imposto sobre o imóvel usado pela empresa. Não é imposto sobre vendas.',
    Segurança: 'Despesa operacional: vigilância, monitoramento e alarme do estabelecimento.',
    Utilidades: 'Despesa operacional: consumo de energia, água, telefone e internet pela empresa.',
    'Energia elétrica': 'Despesa operacional: consumo de eletricidade do estabelecimento.',
    Água: 'Despesa operacional: consumo de água e cobrança de esgoto do estabelecimento.',
    'Telefone e internet':
      'Despesa operacional: telefonia e conexão de internet usadas pela empresa.',
    'Serviços de terceiros':
      'Despesa operacional: serviços contratados de outras empresas ou profissionais, como contabilidade e suporte técnico.',
    Contabilidade:
      'Despesa operacional: honorários e serviços do escritório contábil. Não use para os tributos que ele orientou pagar.',
    'Sistema de gestão e tecnologia':
      'Despesa operacional: assinaturas de software, hospedagem e suporte de tecnologia. Compra de computador exige outra classificação.',
    'Transporte e deslocamento':
      'Despesa operacional: viagens de trabalho, corridas por aplicativo, passagens e deslocamentos a serviço da empresa.',
    'Outras despesas operacionais':
      'Gastos para manter a empresa que não cabem nas demais despesas operacionais. Não use como destino automático de estoque, equipamentos ou retiradas dos sócios.',
    'Despesas financeiras':
      'Gastos com bancos, crédito e meios de pagamento, como tarifas e juros. O principal de um empréstimo tem outra classificação.',
    'Tarifas bancárias':
      'Despesa financeira: cobranças do banco por serviços, como manutenção da conta financeira e transferências.',
    'Taxa de cartão':
      'Despesa financeira: taxa cobrada pela operadora para processar a venda no cartão. Não é desconto concedido ao cliente.',
    'Antecipação de recebíveis':
      'Despesa financeira: custo cobrado para receber antes do prazo uma venda a prazo. Não use para o valor total antecipado.',
    'Juros, multas e IOF':
      'Despesa financeira: juros, multas por atraso e IOF de operações financeiras. O valor original da dívida tem outra classificação.',
    'Receitas financeiras': 'Ganhos financeiros, como rendimentos de aplicações e juros recebidos.',
    'Rendimentos e juros recebidos':
      'Receita financeira: rendimento de aplicações ou juros recebidos. Resgatar o valor aplicado tem outra classificação.',
    'Retiradas de sócios':
      'Fora do resultado: retirada de recursos por sócio. Remuneração pelo trabalho é Pró-labore; distribuição de lucro tem opção própria.',
    'Distribuição de lucros':
      'Fora do resultado: pagamento aos sócios de lucros apurados. Não é salário nem remuneração pelo trabalho.',
    'Aportes de sócios':
      'Fora do resultado: recursos colocados pelos sócios na empresa. Não são receita de vendas.',
    'Transferências entre contas próprias':
      'Fora do resultado: dinheiro movido entre contas financeiras da mesma empresa. Não é nova receita nem despesa.',
    'Aplicações em investimentos':
      'Fora do resultado: dinheiro transferido para uma aplicação financeira. Não é despesa operacional.',
    'Resgates de investimentos':
      'Fora do resultado: retorno do valor aplicado. Os rendimentos têm classificação própria.',
    'Empréstimos tomados':
      'Fora do resultado: dinheiro recebido de um empréstimo. Não é receita de vendas.',
    'Amortização de empréstimos':
      'Fora do resultado: devolução do principal de um empréstimo. Juros, multas e IOF têm classificação separada.',
    'Reembolso a sócios':
      'Fora do resultado: devolução de valor que um sócio adiantou. Ao cadastrar a compra original, escolha o que foi comprado para não contar a despesa duas vezes.',
  }).map(([name, explanation]) => [fold(name), explanation]),
);

function groupGuidance(code: string): string {
  const group = accountGroupOf(code);
  const guidance: Record<string, string> = {
    '1': 'Receitas: valores de vendas de produtos e serviços.',
    '2': 'Deduções da receita: reduzem o faturamento bruto, como impostos sobre vendas, descontos e devoluções. Gastos para manter a empresa ficam em Despesas operacionais.',
    '3': 'Custos: recursos usados nos produtos vendidos ou serviços prestados. Comprar para estoque não significa reconhecer todo o valor como custo agora.',
    '4': 'Despesas operacionais: gastos para manter a empresa, como pessoal, aluguel e internet. Impostos sobre vendas, descontos ao cliente e devoluções ficam em Deduções da receita.',
    '5': 'Resultado financeiro: tarifas, juros e rendimentos. O principal de empréstimos e aplicações fica fora do resultado.',
    '9': 'Fora do resultado: movimentações patrimoniais, como aportes, empréstimos e transferências próprias. Não representam nova receita ou despesa.',
  };
  return `${guidance[group?.code ?? ''] ?? 'Classificação criada pela empresa.'} Confira a finalidade desta conta do plano no plano da empresa.`;
}
